import { toLocalDayKey } from '@/lib/date';
import type { TimeEntryItem } from '@hygge/shared';
import { viewerTimeZone } from '@/lib/timezone';
import { dayKeyWindow, entryDayKey, entryStartWall } from './wall-clock';

/** Monday of the week `date` falls in, at local midnight. */
function mondayOf(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * The week the Time Tracker page opens: the one `?week=YYYY-MM-DD` names, or
 * the current week when there is no key or it is not a real date.
 *
 * The key is a calendar date and is read on the local calendar. It used to go
 * through `new Date(key)`, which is UTC midnight: west of UTC that instant is
 * still the previous Sunday, so the page opened the week before, the next-week
 * arrow wrote the same key back and stood still, and the previous-week arrow
 * skipped a week. [HW-929]
 */
export function pickerWeekStart(week: string | undefined, now: Date): Date {
  const match = week ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(week) : null;
  if (match) {
    const [year, month, day] = [Number(match[1]), Number(match[2]) - 1, Number(match[3])];
    const date = new Date(year, month, day);
    // `new Date(2026, 1, 31)` is 3 March: a key naming a day that does not
    // exist is refused rather than rolled into another month.
    if (date.getFullYear() === year && date.getMonth() === month && date.getDate() === day) {
      return mondayOf(date);
    }
  }
  return mondayOf(now);
}

/**
 * The `?week=` key one arrow press writes: the Monday `delta` weeks from
 * `weekStart`, as a local calendar date. `setDate` walks the local calendar,
 * so a DST change in between cannot land it on a Sunday; `pickerWeekStart`
 * reads the key back as the same local date. [HW-929]
 */
export function stepWeekKey(weekStart: Date, delta: number): string {
  const next = new Date(weekStart);
  next.setDate(next.getDate() + delta * 7);
  return toLocalDayKey(next);
}

/**
 * The week's seven day keys, Monday first, named in the viewer's own calendar.
 *
 * The keys used to be built with `toISOString().split('T')[0]` from local
 * midnights, which names the UTC day of that instant: east of UTC every local
 * midnight is still "yesterday" in UTC, so all seven rows carried the previous
 * day's date, disagreed with the week header beside them (which was computed
 * locally), and put "Today" on the wrong row. This was the deliberately-open
 * half of HW-834; the local calendar day is the only reading that matches the
 * header, the row labels and the day the viewer would name. [HW-858]
 */
export function buildWeekDayKeys(weekStart: Date): string[] {
  const keys: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    keys.push(toLocalDayKey(d));
  }
  return keys;
}

/**
 * The `from`/`to` the timesheet asks the API for: the viewer's picker week —
 * "my week", seven local calendar days — sent as UTC-digit day edges. The
 * server files an entry under `started_at AT TIME ZONE 'UTC'`, which is the
 * author's wall day, so a local midnight through `toISOString()` would cut
 * the week hours off one end and add them to the other. [HW-916]
 */
export function weekWindow(weekStart: Date): { from: string; to: string } {
  const keys = buildWeekDayKeys(weekStart);
  return dayKeyWindow(keys[0], keys[6]);
}

/** A `YYYY-MM-DD` key moved by whole days, on the calendar the key names. */
function shiftDayKey(key: string, days: number): string {
  const d = new Date(`${key}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * The `from`/`to` the Weekly Timesheet's entry LIST asks for: the week's
 * UTC-digit edges widened by one day on each side.
 *
 * A legacy row (null zone) is filed on the viewer's day while the server
 * matches `from`/`to` against its raw instant: a Kyiv row at Sunday 21:30Z is
 * the viewer's Monday 00:30, outside that Monday's exact window and, in the
 * week before, without a row to go in, so it showed in neither week. Zone-less
 * rows a stale tab writes behave the same. No viewer zone is more than a day
 * off UTC, so one day on each side reaches every such row, and
 * `groupEntriesByDay` then drops whatever the extra days bring in that belongs
 * to a neighbouring week. A zoned row's day is its digits, which the exact
 * window already covers, so the widening changes nothing for it. The totals
 * keep `weekWindow`: see `TimeSummary`. [HW-916]
 */
export function weekFetchWindow(weekStart: Date): { from: string; to: string } {
  const keys = buildWeekDayKeys(weekStart);
  return dayKeyWindow(shiftDayKey(keys[0], -1), shiftDayKey(keys[6], 1));
}

/**
 * The day the week opens on when there is nothing more specific to choose:
 * today when this week holds it, otherwise the week's Monday. The Weekly
 * Timesheet uses it for both which day to expand and which date Log Time
 * defaults to, so a viewed past week backfills into itself, not into today.
 * [HW-858] [HW-868]
 */
export function weekDefaultDay(weekStart: Date, todayKey: string): string {
  const weekDays = buildWeekDayKeys(weekStart);
  return weekDays.includes(todayKey) ? todayKey : weekDays[0];
}

/**
 * The week's entries bucketed per day, every bucket present even when empty,
 * rows inside a day earliest-first.
 *
 * An entry belongs to the day its `startedAt` digits name — the author's own
 * wall day, stored as UTC digits — not the day that instant falls on in the
 * viewer's zone: read locally, an entry typed at 00:30 filed under the
 * previous day's row west of UTC, and one typed at 23:30 under the next day's
 * east of it. The rows themselves stay the viewer's calendar week. [HW-916]
 *
 * A legacy row (null zone) is the exception until the backfill converts it —
 * and a stale tab can go on writing them after this frontend ships — because
 * its `startedAt` is still a real instant: it is filed by that instant read in
 * the viewer's zone, the day it sat under before HW-916, not by its raw digits.
 *
 * The sort matches the server's: `startedAt`, then `createdAt` to break the
 * tie manual entries all share (their forms stamp 09:00), so the order cannot
 * depend on the query plan or the page the API returned. `startedAt` as the
 * row prints it, though, so a legacy row among converted ones sits where its
 * clock says rather than where its raw instant would put it.
 *
 * `viewerZone` is for a caller that already resolved the zone once for the
 * whole render (the Weekly Timesheet also reads it for every row it prints,
 * so it hands over the one it already has). Left out, it is looked up here
 * instead — still once for the week, never per row. [HW-916]
 */
export function groupEntriesByDay(
  weekStart: Date,
  entries: TimeEntryItem[],
  viewerZone?: string,
): Map<string, TimeEntryItem[]> {
  const grouped = new Map<string, TimeEntryItem[]>();
  for (const key of buildWeekDayKeys(weekStart)) {
    grouped.set(key, []);
  }

  // Each row's reading is taken once, with the zone resolved once for the whole
  // week, and the sort below compares those readings: asked per comparison, a
  // week of legacy rows went back to `Intl` O(n log n) times. [HW-916]
  const zone = viewerZone ?? viewerTimeZone();
  const startWall = new Map<TimeEntryItem, number>();

  for (const entry of entries) {
    const wall = entryStartWall(entry, zone);
    startWall.set(entry, new Date(wall).getTime());
    const dateKey = entryDayKey(wall);
    // Only the week's own seven days get a bucket, so a row the widened fetch
    // (`weekFetchWindow`) brought in from a neighbouring week is dropped here.
    const existing = grouped.get(dateKey);
    if (existing) {
      existing.push(entry);
    }
  }

  for (const dayEntries of grouped.values()) {
    dayEntries.sort(
      (a, b) =>
        (startWall.get(a) ?? 0) - (startWall.get(b) ?? 0) ||
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
  }

  return grouped;
}
